import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react'
import { Icon } from './Icon'

type ToastTone = 'positive' | 'warning' | 'danger' | 'neutral'

interface ToastOptions {
  tone?: ToastTone
  /** e.g. Undo. A toast with an action stays up longer so there is time to use it. */
  action?: { label: string; onClick: () => void }
}

interface ToastItem extends Required<Pick<ToastOptions, 'tone'>> {
  id: number
  message: string
  action?: ToastOptions['action']
}

type Notify = (message: string, options?: ToastOptions) => void

const ToastContext = createContext<Notify>(() => {})

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const seq = useRef(0)

  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), [])

  const notify = useCallback<Notify>(
    (message, options = {}) => {
      const id = ++seq.current
      setItems((xs) => [...xs.slice(-3), { id, message, tone: options.tone ?? 'neutral', action: options.action }])
      window.setTimeout(() => dismiss(id), options.action ? 8000 : 5000)
    },
    [dismiss],
  )

  return (
    <ToastContext.Provider value={notify}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`toast toast-${t.tone}`}>
            <span>{t.message}</span>
            {t.action ? (
              <button
                className="toast-action"
                onClick={() => {
                  t.action!.onClick()
                  dismiss(t.id)
                }}
              >
                {t.action.label}
              </button>
            ) : null}
            <button className="icon-btn-plain" aria-label="Dismiss" onClick={() => dismiss(t.id)}>
              <Icon name="close" size={16} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export const useToast = () => useContext(ToastContext)
